import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import ServiceSelector from './ServiceSelector';
import { Calendar, RefreshCw, ChevronDown, ChevronUp, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

// Booking Progress Bar Component (for both Create and Reschedule flows)
const BookingProgressBar = ({ currentStep, steps, title, subtitle }) => {
  const progress = ((currentStep) / steps.length) * 100;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4">
        <div className="text-center mb-6">
          <RefreshCw className="w-12 h-12 text-blue-500 mx-auto mb-3 animate-spin" />
          <h3 className="text-xl font-semibold text-gray-800">{title}</h3>
          <p className="text-gray-500 text-sm mt-1">{subtitle}</p>
        </div>
        
        {/* Progress Bar */}
        <div className="mb-6">
          <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-blue-400 to-blue-600 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-right text-sm text-gray-500 mt-1">{Math.round(progress)}%</p>
        </div>
        
        {/* Steps List */}
        <div className="space-y-3">
          {steps.map((step, index) => {
            const isComplete = index < currentStep;
            const isCurrent = index === currentStep;
            
            return (
              <div 
                key={index}
                className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                  isCurrent ? 'bg-blue-50 border border-blue-200' : 
                  isComplete ? 'bg-green-50' : 'bg-gray-50'
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isComplete ? 'bg-green-500 text-white' :
                  isCurrent ? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-600'
                }`}>
                  {isComplete ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <span className="text-sm font-medium">{index + 1}</span>
                  )}
                </div>
                <div className="flex-1">
                  <p className={`font-medium ${
                    isCurrent ? 'text-blue-700' : 
                    isComplete ? 'text-green-700' : 'text-gray-500'
                  }`}>
                    {step.label}
                  </p>
                  {isCurrent && step.sublabel && (
                    <p className="text-xs text-blue-500 mt-0.5">{step.sublabel}</p>
                  )}
                </div>
                {isCurrent && (
                  <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const AppointmentModal = ({ calendarId, calendar, selectedSlot, services, onClose, getAuthHeaders, isCustomer }) => {
  const { runningUser } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [servers, setServers] = useState([]);
  const [formData, setFormData] = useState({
    customer_ids: [],  // Changed to array to support multiple customers
    service_id: '',
    server_id: '',
    notes: ''
  });
  const [selectedService, setSelectedService] = useState(null);
  const [startTime, setStartTime] = useState(selectedSlot?.time || '09:00');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Check if selected service allows multiple customers
  const allowsMultipleCustomers = selectedService && selectedService.customers_allowed > 1;
  const maxCustomers = selectedService?.customers_allowed || 1;
  
  // Booking Progress State (for both Create and Reschedule flows)
  const [bookingProgress, setBookingProgress] = useState({
    active: false,
    currentStep: 0,
    mode: 'create' // 'create' or 'reschedule'
  });
  
  // Steps for different booking modes
  const createSteps = [
    { label: 'Creating appointment', sublabel: 'Reserving your time slot...' },
    { label: 'Confirming booking', sublabel: 'Finalizing your appointment...' }
  ];
  
  const rescheduleSteps = [
    { label: 'Creating new appointment', sublabel: 'Reserving your new time slot...' },
    { label: 'Removing old booking', sublabel: 'Cleaning up the previous appointment...' }
  ];
  
  // RESCHEDULE FEATURE STATE (Jan 15, 2026)
  const [existingBookings, setExistingBookings] = useState([]);
  const [showReplaceSection, setShowReplaceSection] = useState(false);
  const [selectedReplacement, setSelectedReplacement] = useState(null);
  const [loadingBookings, setLoadingBookings] = useState(false);

  useEffect(() => {
    if (isCustomer && runningUser) {
      // For customers, pre-fill their info and fetch their last service
      setFormData(prev => ({ ...prev, customer_ids: [runningUser.id] }));
      fetchLastBookedService();
      // RESCHEDULE: Fetch existing bookings for replacement option
      fetchExistingBookings();
    } else {
      // For admins, fetch customers list
      fetchCustomers();
    }
    
    // Fetch servers if calendar supports multiple servers
    if (calendar?.multiple_servers) {
      fetchServers();
    }
    
    // Set start time from selected slot
    if (selectedSlot?.time) {
      setStartTime(selectedSlot.time);
    }
  }, [isCustomer, runningUser, calendar, selectedSlot]);

  // RESCHEDULE FEATURE (Jan 15, 2026):
  // Fetch customer's existing non-expired bookings for replacement option
  const fetchExistingBookings = async () => {
    if (!runningUser?.id) return;
    
    setLoadingBookings(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/appointments`, {
        params: { calendar_id: calendarId },
        headers: getAuthHeaders()
      });
      
      const now = new Date();
      // Filter for customer's future/current bookings (non-expired, non-cancelled)
      const customerBookings = (response.data.data || [])
        .filter(apt => {
          const aptDate = new Date(apt.start_time);
          return apt.customer_id === runningUser.id &&
                 aptDate >= now &&
                 apt.status !== 'cancelled' &&
                 apt.status !== 'no_show' &&
                 apt.status !== 'completed';
        })
        .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
      
      setExistingBookings(customerBookings);
    } catch (err) {
      console.error('Error fetching existing bookings:', err);
    } finally {
      setLoadingBookings(false);
    }
  };

  // RESCHEDULE: Handle selection of booking to replace
  const handleSelectReplacement = (booking) => {
    setSelectedReplacement(booking);
    
    // Pre-fill form with the booking's service, server, and notes
    setFormData(prev => ({
      ...prev,
      service_id: booking.service_id || '',
      server_id: booking.server_id || '',
      notes: booking.notes || ''
    }));
    
    // Set the selected service for duration calculation
    const service = services.find(s => s.id === booking.service_id);
    if (service) {
      setSelectedService(service);
    }
  };

  // RESCHEDULE: Clear replacement selection
  const clearReplacement = () => {
    setSelectedReplacement(null);
    // Keep current form data, user might want to adjust
  };

  // Format date/time for display
  const formatBookingDateTime = (dateTimeStr) => {
    const date = new Date(dateTimeStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Auto-select service if only one service is available
  useEffect(() => {
    if (services && services.length === 1 && !formData.service_id) {
      setFormData(prev => ({ ...prev, service_id: services[0].id }));
      setSelectedService(services[0]);
    }
  }, [services]);

  // Auto-select server if only one server is available
  useEffect(() => {
    if (servers && servers.length === 1 && !formData.server_id && calendar?.multiple_servers) {
      setFormData(prev => ({ ...prev, server_id: servers[0].id }));
    }
  }, [servers, calendar]);

  const fetchLastBookedService = async () => {
    try {
      // Fetch customer's appointments to find last booked service
      const appointmentsRes = await axios.get(`${API_BASE_URL}/appointments`, {
        params: { calendar_id: calendarId },
        headers: getAuthHeaders()
      });
      
      // Filter appointments for this customer and sort by start time (most recent first)
      const customerAppointments = (appointmentsRes.data.data || [])
        .filter(apt => apt.customer_id === runningUser.id)
        .sort((a, b) => new Date(b.start_time) - new Date(a.start_time));
      
      if (customerAppointments.length > 0) {
        const lastServiceId = customerAppointments[0].service_id;
        setFormData(prev => ({ ...prev, service_id: lastServiceId }));
        
        const service = services.find(s => s.id === lastServiceId);
        setSelectedService(service);
      }
    } catch (err) {
      console.error('Error fetching last booked service:', err);
      // Not critical, just don't pre-select
    }
  };

  const fetchCustomers = async () => {
    try {
      // Fetch customers registered to THIS calendar only (same as ReleaseSlotModal)
      const customersUrl = calendarId 
        ? `${API_BASE_URL}/calendars/${calendarId}/customers`
        : `${API_BASE_URL}/admin/users`;
      
      const customersRes = await axios.get(customersUrl, {
        headers: getAuthHeaders()
      });
      
      // Handle both response formats and filter to customers only
      const allCustomers = (customersRes.data.data || [])
        .filter(u => u.role === 'customer' || u.role === 'non_registered_customer')
        .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
      
      // Get existing appointments for this time slot to filter out already-booked customers
      if (selectedSlot) {
        const slotStartTime = `${selectedSlot.date}T${selectedSlot.time}:00`;
        
        const appointmentsRes = await axios.get(`${API_BASE_URL}/appointments`, {
          params: {
            calendar_id: calendarId,
            start_date: selectedSlot.date,
            end_date: selectedSlot.date
          },
          headers: getAuthHeaders()
        });
        
        // Get customer IDs who already have appointments at this exact time
        const bookedCustomerIds = (appointmentsRes.data.data || [])
          .filter(apt => apt.start_time === slotStartTime && apt.status !== 'cancelled')
          .map(apt => apt.customer_id);
        
        // Filter out customers who already have bookings at this time
        const availableCustomers = allCustomers.filter(
          customer => !bookedCustomerIds.includes(customer.id)
        );
        
        setCustomers(availableCustomers);
      } else {
        setCustomers(allCustomers);
      }
    } catch (err) {
      console.error('Error fetching customers:', err);
      setError('Failed to load customers');
    }
  };

  const fetchServers = async () => {
    try {
      const serversRes = await axios.get(`${API_BASE_URL}/calendar-servers/${calendarId}/servers`, {
        headers: getAuthHeaders()
      });
      
      // Get assigned servers only
      const assignedServers = serversRes.data.data.assigned || [];
      
      // Filter servers to show only those available for the selected time slot
      if (selectedSlot && assignedServers.length > 0) {
        // Step 1: Filter to active servers only
        const activeServers = assignedServers.filter(s => s.is_active === true);
        
        if (activeServers.length > 0) {
          // Step 2: Get server availability data for this time slot
          const availabilityResponse = await axios.get(`${API_BASE_URL}/availability`, {
            params: {
              calendar_id: calendarId,
              start_date: selectedSlot.date,
              end_date: selectedSlot.date
            },
            headers: getAuthHeaders()
          });
          
          const availabilitySlots = availabilityResponse.data.data || [];
          const bookingHour = parseInt(selectedSlot.time.split(':')[0]);
          
          // Step 3: Find servers working at this time
          const workingServerIds = availabilitySlots
            .filter(avail => {
              if (!avail.server_id) return false;
              
              const serverStartHour = parseInt(avail.start_time.split(':')[0]);
              const serverEndHour = parseInt(avail.end_time.split(':')[0]);
              
              // Check if booking hour falls within server's working hours
              return bookingHour >= serverStartHour && bookingHour < serverEndHour;
            })
            .map(avail => avail.server_id);
          
          // Step 4: Get appointments for this slot to see which servers are already booked
          const slotAppointments = await axios.get(`${API_BASE_URL}/appointments`, {
            params: {
              calendar_id: calendarId,
              start_date: selectedSlot.date,
              end_date: selectedSlot.date
            },
            headers: getAuthHeaders()
          });
          
          const startTime = `${selectedSlot.date}T${selectedSlot.time}:00`;
          const bookedServerIds = (slotAppointments.data.data || [])
            .filter(apt => apt.start_time === startTime && apt.server_id)
            .map(apt => apt.server_id);
          
          // Step 5: Filter to servers that are active, working, AND not booked
          const availableServers = activeServers.filter(s => 
            workingServerIds.includes(s.id) && !bookedServerIds.includes(s.id)
          );
          
          setServers(availableServers);
        } else {
          setServers([]);
        }
      } else {
        setServers(assignedServers);
      }
    } catch (err) {
      console.error('Error fetching servers:', err);
      // Don't show error to user, just don't show server selection
    }
  };

  const handleServiceChange = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    
    // When service changes, reset customer selection if switching between single/multi
    const newAllowsMultiple = service && service.customers_allowed > 1;
    const currentAllowsMultiple = selectedService && selectedService.customers_allowed > 1;
    
    if (newAllowsMultiple !== currentAllowsMultiple) {
      // Reset customer selection when switching modes
      setFormData(prev => ({ 
        ...prev, 
        service_id: serviceId,
        customer_ids: [] 
      }));
    } else {
      setFormData(prev => ({ ...prev, service_id: serviceId }));
    }
    
    setSelectedService(service);
  };

  // Handle customer toggle for multi-select mode
  const handleCustomerToggle = (customerId) => {
    setFormData(prev => {
      const isSelected = prev.customer_ids.includes(customerId);
      let newCustomerIds;
      
      if (isSelected) {
        // Remove customer
        newCustomerIds = prev.customer_ids.filter(id => id !== customerId);
      } else {
        // Add customer (respecting max limit)
        if (prev.customer_ids.length < maxCustomers) {
          newCustomerIds = [...prev.customer_ids, customerId];
        } else {
          // At max capacity - don't add
          return prev;
        }
      }
      
      return { ...prev, customer_ids: newCustomerIds };
    });
  };

  // Handle single customer selection (radio button mode)
  const handleSingleCustomerSelect = (customerId) => {
    setFormData(prev => ({ ...prev, customer_ids: [customerId] }));
  };

  // Generate 15-minute interval options for a given hour
  const generate15MinuteIntervals = (hour) => {
    return [
      `${hour.toString().padStart(2, '0')}:00`,
      `${hour.toString().padStart(2, '0')}:15`,
      `${hour.toString().padStart(2, '0')}:30`,
      `${hour.toString().padStart(2, '0')}:45`
    ];
  };

  const calculateEndTime = () => {
    if (!selectedService) return '';
    
    const durationMinutes = selectedService.service_duration || 60;
    
    if (calendar?.booking_type === 'floating') {
      // For floating calendars, round up to nearest 15 minutes
      const [hours, minutes] = startTime.split(':').map(Number);
      const totalMinutes = hours * 60 + minutes + durationMinutes;
      const roundedMinutes = Math.ceil(totalMinutes / 15) * 15;
      const endHours = Math.floor(roundedMinutes / 60);
      const endMins = roundedMinutes % 60;
      return `${endHours.toString().padStart(2, '0')}:${endMins.toString().padStart(2, '0')}`;
    } else {
      // For fixed calendars, use hourly calculation
      const startHour = selectedSlot?.hour || 0;
      const endHour = startHour + Math.ceil(durationMinutes / 60);
      return `${endHour.toString().padStart(2, '0')}:00`;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    // Determine if this is a reschedule or create operation
    const isRescheduling = !!selectedReplacement;
    
    // Show progress bar for both create and reschedule flows
    setBookingProgress({ 
      active: true, 
      currentStep: 0, 
      mode: isRescheduling ? 'reschedule' : 'create' 
    });

    try {
      const appointmentStartTime = `${selectedSlot.date}T${startTime}:00`;
      const appointmentEndTime = `${selectedSlot.date}T${calculateEndTime()}:00`;

      // Create appointments for each selected customer
      // For single customer services, this will just be one appointment
      const customerIds = formData.customer_ids;
      
      if (customerIds.length === 0) {
        setError('Please select at least one customer');
        setLoading(false);
        setBookingProgress({ active: false, currentStep: 0, mode: 'create' });
        return;
      }

      // For multiple customers, create separate appointments for each
      for (let i = 0; i < customerIds.length; i++) {
        const customerId = customerIds[i];
        
        const appointmentData = {
          calendar_id: calendarId,
          customer_id: customerId,
          service_id: formData.service_id,
          start_time: appointmentStartTime,
          end_time: appointmentEndTime,
          status: 'scheduled',
          notes: formData.notes
        };

      // Handle server assignment for Multiple Servers mode
      if (calendar?.multiple_servers) {
        if (formData.server_id) {
          // User manually selected a server
          appointmentData.server_id = formData.server_id;
        } else if (servers.length > 0) {
          // Auto-assign using round-robin - only from truly available servers
          
          // Step 1: Filter to active servers only
          const activeServers = servers.filter(s => s.is_active === true);
          
          if (activeServers.length > 0) {
            // Step 2: Get server availability data for this time slot
            const availabilityResponse = await axios.get(`${API_BASE_URL}/availability`, {
              params: {
                calendar_id: calendarId,
                start_date: selectedSlot.date,
                end_date: selectedSlot.date
              },
              headers: getAuthHeaders()
            });
            
            const availabilitySlots = availabilityResponse.data.data || [];
            const bookingHour = parseInt(appointmentStartTime.split('T')[1].split(':')[0]);
            
            // Step 3: Find servers working at this time
            const workingServerIds = availabilitySlots
              .filter(avail => {
                if (!avail.server_id) return false;
                
                const serverStartHour = parseInt(avail.start_time.split(':')[0]);
                const serverEndHour = parseInt(avail.end_time.split(':')[0]);
                
                // Check if booking hour falls within server's working hours
                return bookingHour >= serverStartHour && bookingHour < serverEndHour;
              })
              .map(avail => avail.server_id);
            
            // Step 4: Get appointments for this slot to see which servers are already booked
            const slotAppointments = await axios.get(`${API_BASE_URL}/appointments`, {
              params: {
                calendar_id: calendarId,
                start_date: selectedSlot.date,
                end_date: selectedSlot.date
              },
              headers: getAuthHeaders()
            });
            
            const bookedServerIds = (slotAppointments.data.data || [])
              .filter(apt => apt.start_time === appointmentStartTime && apt.server_id)
              .map(apt => apt.server_id);
            
            // Step 5: Find servers that are active, working, AND not booked
            const availableServers = activeServers.filter(s => 
              workingServerIds.includes(s.id) && !bookedServerIds.includes(s.id)
            );
            
            if (availableServers.length > 0) {
              // Step 6: Round-robin - select the server with the least bookings today
              const todayAppointments = slotAppointments.data.data || [];
              const serverBookingCounts = {};
              availableServers.forEach(s => {
                serverBookingCounts[s.id] = todayAppointments.filter(apt => 
                  apt.server_id === s.id
                ).length;
              });
              
              // Sort by booking count (ascending) and pick the first one
              const sortedServers = availableServers.sort((a, b) => 
                serverBookingCounts[a.id] - serverBookingCounts[b.id]
              );
              
              appointmentData.server_id = sortedServers[0].id;
            }
          }
        }
      }

        // Create the appointment for this customer
        await axios.post(
          `${API_BASE_URL}/appointments`,
          appointmentData,
          { headers: getAuthHeaders() }
        );
      } // End of customer loop

      // RESCHEDULE: If replacing an existing booking, move to step 2 and delete the old one
      if (isRescheduling) {
        // Move to step 2: Removing old booking
        setBookingProgress({ active: true, currentStep: 1, mode: 'reschedule' });
        
        // Small delay to let the UI update and show the transition
        await new Promise(resolve => setTimeout(resolve, 500));
        
        try {
          await axios.delete(
            `${API_BASE_URL}/appointments/${selectedReplacement.id}`,
            { headers: getAuthHeaders() }
          );
          console.log(`Deleted replaced booking: ${selectedReplacement.id}`);
        } catch (deleteErr) {
          // Log error but don't fail - new appointment was already created
          console.error('Error deleting replaced booking:', deleteErr);
        }
        
        // Mark as complete (step 2 done)
        setBookingProgress({ active: true, currentStep: 2, mode: 'reschedule' });
        
        // Brief delay to show completion before closing
        await new Promise(resolve => setTimeout(resolve, 800));
      } else {
        // For regular create: move to step 2 (confirming)
        setBookingProgress({ active: true, currentStep: 1, mode: 'create' });
        
        // Brief delay to show completion state
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Mark as complete
        setBookingProgress({ active: true, currentStep: 2, mode: 'create' });
        
        // Brief delay to show completion before closing
        await new Promise(resolve => setTimeout(resolve, 600));
      }

      onClose();
    } catch (err) {
      console.error('Error creating appointment:', err);
      setError(err.response?.data?.detail || 'Failed to create appointment');
      // Reset progress on error
      setBookingProgress({ active: false, currentStep: 0, mode: 'create' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Booking Progress Bar Overlay (for both Create and Reschedule) */}
      {bookingProgress.active && (
        <BookingProgressBar 
          currentStep={bookingProgress.currentStep} 
          steps={bookingProgress.mode === 'reschedule' ? rescheduleSteps : createSteps}
          title={bookingProgress.mode === 'reschedule' ? 'Rescheduling Your Appointment' : 'Creating Your Appointment'}
          subtitle={bookingProgress.mode === 'reschedule' ? 'Please wait while we update your booking...' : 'Please wait while we confirm your booking...'}
        />
      )}
      
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              {selectedReplacement ? 'Reschedule Appointment' : 'Create Appointment'}
            </h2>
            <p className="text-gray-600 mt-1">
              {selectedSlot?.date} at {calendar?.booking_type === 'floating' ? startTime : selectedSlot?.time}
            </p>
            {selectedReplacement && (
              <p className="text-sm text-blue-600 mt-1 flex items-center gap-1">
                <RefreshCw className="w-4 h-4" />
                Moving from {formatBookingDateTime(selectedReplacement.start_time)}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          {error && (
            <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {/* RESCHEDULE SECTION (Jan 15, 2026): 
              Show "Replace existing booking" option for customers with existing bookings */}
          {isCustomer && existingBookings.length > 0 && (
            <div className="border border-blue-200 rounded-lg bg-blue-50">
              <button
                type="button"
                onClick={() => setShowReplaceSection(!showReplaceSection)}
                className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-blue-100 rounded-lg transition-colors"
              >
                <div className="flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-blue-800">
                    {selectedReplacement 
                      ? `Replacing: ${formatBookingDateTime(selectedReplacement.start_time)}`
                      : `Replace existing booking (${existingBookings.length} available)`
                    }
                  </span>
                </div>
                {showReplaceSection ? (
                  <ChevronUp className="w-5 h-5 text-blue-600" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-blue-600" />
                )}
              </button>
              
              {showReplaceSection && (
                <div className="px-4 pb-4 space-y-2">
                  <p className="text-sm text-blue-700 mb-2">
                    Select a booking to reschedule to this time slot:
                  </p>
                  
                  {loadingBookings ? (
                    <div className="text-center py-2 text-gray-500">Loading...</div>
                  ) : (
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {existingBookings.map(booking => (
                        <div
                          key={booking.id}
                          onClick={() => handleSelectReplacement(booking)}
                          className={`p-3 rounded-md border cursor-pointer transition-all ${
                            selectedReplacement?.id === booking.id
                              ? 'border-blue-500 bg-blue-100 ring-2 ring-blue-300'
                              : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium text-gray-800 text-sm">
                                {formatBookingDateTime(booking.start_time)}
                              </div>
                              <div className="text-xs text-gray-600">
                                {booking.service_name}
                              </div>
                            </div>
                            {selectedReplacement?.id === booking.id && (
                              <span className="text-xs bg-blue-500 text-white px-2 py-1 rounded">
                                Selected
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {selectedReplacement && (
                    <button
                      type="button"
                      onClick={clearReplacement}
                      className="text-sm text-red-600 hover:text-red-800 underline mt-2"
                    >
                      Clear selection (create new booking instead)
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Start Time Selector (for floating calendars only) */}
          {calendar?.booking_type === 'floating' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Time *
              </label>
              <select
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              >
                {generate15MinuteIntervals(selectedSlot?.hour || 9).map(time => (
                  <option key={time} value={time}>
                    {time}
                  </option>
                ))}
              </select>
              <p className="text-xs text-purple-600 mt-1">
                Select any 15-minute interval for flexible scheduling
              </p>
            </div>
          )}

          {/* SERVICE SELECTION - Moved above Customer */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Service *
            </label>
            <ServiceSelector
              services={services}
              selectedServiceId={formData.service_id}
              onServiceChange={handleServiceChange}
              required
            />
          </div>

          {selectedService && (
            <div className="bg-gray-50 p-3 rounded-md">
              <p className="text-sm text-gray-600">
                <strong>Duration:</strong> {selectedService.service_duration} minutes
              </p>
              <p className="text-sm text-gray-600">
                <strong>End Time:</strong> {calculateEndTime()}
              </p>
              {allowsMultipleCustomers && (
                <p className="text-sm text-blue-600 mt-1">
                  <strong>Note:</strong> This service allows up to {maxCustomers} participants
                </p>
              )}
            </div>
          )}

          {/* CUSTOMER SELECTION - Now below Service with dynamic single/multi mode */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {allowsMultipleCustomers ? (
                <>Customer(s) * <span className="text-gray-400 font-normal">({formData.customer_ids.length}/{maxCustomers} selected)</span></>
              ) : (
                'Customer *'
              )}
            </label>
            {isCustomer ? (
              // Display-only field for customers
              <div className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-700 font-mono text-sm">
                👤 {runningUser?.full_name} | {runningUser?.email}
              </div>
            ) : !formData.service_id ? (
              // Prompt to select service first
              <div className="p-4 text-center text-gray-500 border border-gray-200 rounded-md bg-gray-50">
                Please select a service first
              </div>
            ) : (
              // Dynamic selection based on service's customers_allowed
              <div className="border border-gray-200 rounded-md max-h-48 overflow-y-auto bg-white">
                {customers.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">No customers available</div>
                ) : (
                  <div className="divide-y">
                    {customers.map(customer => {
                      const isSelected = formData.customer_ids.includes(customer.id);
                      const isDisabled = !isSelected && formData.customer_ids.length >= maxCustomers;
                      
                      return (
                        <label
                          key={customer.id}
                          className={`flex items-center p-3 cursor-pointer transition-colors ${
                            isSelected 
                              ? 'bg-yellow-50 hover:bg-yellow-100' 
                              : isDisabled
                                ? 'bg-gray-100 cursor-not-allowed opacity-50'
                                : 'hover:bg-gray-50'
                          }`}
                        >
                          {allowsMultipleCustomers ? (
                            // Checkbox for multi-select
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleCustomerToggle(customer.id)}
                              disabled={isDisabled}
                              className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500"
                            />
                          ) : (
                            // Radio button for single-select
                            <input
                              type="radio"
                              name="customer_selection"
                              checked={isSelected}
                              onChange={() => handleSingleCustomerSelect(customer.id)}
                              className="w-4 h-4 text-yellow-500 border-gray-300 focus:ring-yellow-500"
                            />
                          )}
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">{customer.full_name}</div>
                            <div className="text-xs text-gray-500">{customer.email}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {calendar?.multiple_servers && servers.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Server (Optional)
              </label>
              <select
                value={formData.server_id}
                onChange={(e) => setFormData({ ...formData, server_id: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              >
                <option value="">Auto-assign server</option>
                {servers.map(server => (
                  <option key={server.id} value={server.id}>
                    {server.server_name} {server.email ? `(${server.email})` : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Leave as &quot;Auto-assign&quot; to let the system choose an available server
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              rows="3"
              placeholder="Any additional notes..."
            />
          </div>

          {/* RESCHEDULE: Show confirmation message when replacing */}
          {selectedReplacement && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
              <p className="text-sm text-amber-800">
                <strong>Rescheduling:</strong> Your booking on{' '}
                <span className="font-semibold">{formatBookingDateTime(selectedReplacement.start_time)}</span>
                {' '}will be moved to this new time slot.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`px-6 py-2 font-semibold rounded-md transition-colors disabled:opacity-50 ${
                selectedReplacement
                  ? 'bg-blue-500 hover:bg-blue-600 text-white'
                  : 'bg-yellow-500 hover:bg-yellow-600 text-white'
              }`}
            >
              {loading 
                ? (selectedReplacement ? 'Rescheduling...' : 'Creating...') 
                : (selectedReplacement ? 'Reschedule Booking' : 'Create Appointment')
              }
            </button>
          </div>
        </form>
      </div>
    </div>
    </>
  );
};

export default AppointmentModal;
